import React, { useMemo } from "react";
import type { LoyaltyTierPrize } from "kadow-jetpack-model";
import KC_Heading2 from "../../../../../designsystem/typography/KC-Heading/KC_Heading2";
import KC_Text from "../../../../../designsystem/typography/KC_Text/KC_Text";
import KC_Toggle from "../../../../../designsystem/KC_FormInput/KC_Toggle";
import KC_Button, {
  KC_ButtonSize
} from "../../../../../designsystem/KC_Button/KC_Button";
import {
  KC_Text_Sizes,
  KC_Weights
} from "../../../../../designsystem/ds_commons";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { tt } from "../../../../../utils/i18n/i18n";
import TwemojiWrapper from "../../../../../components/TwemojiWrapper/TwemojiWrapper";
import PrizesConfiguratorDisplayCard from "../component/PrizesConfiguratorDisplayCard";
import type { Config } from "kadow-jetpack-model";
import { isTierRewardTokens } from "../../Loyalty/helper/defaultLoyaltyTiers";
import Carousel from "../../../../../components/Carousel/Carousel";

const SECTION_DESCRIPTION_CLASS = "text-secondary";

interface LoyaltyTiersSectionProps {
  config: Config;
  onToggleTiersEnabled: () => void;
  onChooseTiersClick: () => void;
}

const choosePrizesButtonBaseProps = {
  size: KC_ButtonSize.Large,
  text: tt("prizes_configurator_choose_my_prizes"),
  isPrimary: true,
  showIconOnTheRight: true,
  icon: faArrowRight,
  extraStyle: { width: "unset !important" as const }
};

const LoyaltyTiersSection: React.FC<LoyaltyTiersSectionProps> = ({
  config,
  onToggleTiersEnabled,
  onChooseTiersClick
}) => {
  const loyaltyTierSlides = useMemo(
    () =>
      (config?.lty?.tiers ?? []).map((tier) => (
        <div key={tier.id} className="prize-preview-container">
          <PrizesConfiguratorDisplayCard
            title={
              isTierRewardTokens(tier.reward)
                ? tt("loyalty_tokens_count", { count: tier.reward })
                : (tier.reward as LoyaltyTierPrize).name ||
                  tt("prizes_configurator_no_name_gift")
            }
            secondaryText={
              tier.threshold === 1
                ? tt("loyalty_threshold_actions", {
                    count: tier.threshold
                  })
                : tt("loyalty_threshold_actions_plural", {
                    count: tier.threshold
                  })
            }
            badge={
              <TwemojiWrapper isBigText={true}>{tier.icon}</TwemojiWrapper>
            }
            media={
              isTierRewardTokens(tier.reward) ? (
                <span className="prizes-configurator-display-card__token-coin">
                  <TwemojiWrapper>🪙</TwemojiWrapper>
                </span>
              ) : (
                <TwemojiWrapper isBigText={true}>
                  {(tier.reward as LoyaltyTierPrize).image || "🎁"}
                </TwemojiWrapper>
              )
            }
          />
        </div>
      )),
    [config?.lty?.tiers]
  );

  return (
    <section className="prizes-config-section">
      <div className="prizes-config-section__header">
        <div className="prizes-config-section__title-block">
          <div className="prizes-config-section__title-row">
            <KC_Heading2 text={tt("loyalty_prizes_section_title")} />
            <KC_Toggle
              id="loyalty-tiers-enabled-toggle"
              isChecked={!!config?.lty?.enabled}
              onToggle={onToggleTiersEnabled}
            />
          </div>
          <KC_Text
            size={KC_Text_Sizes.lg}
            weight={KC_Weights.reg}
            text={tt("loyalty_prizes_section_description")}
            className={SECTION_DESCRIPTION_CLASS}
          />
        </div>
        {!!config?.lty?.enabled && (
          <div className="prizes-config-section__actions">
            <KC_Button
              id="choose-tier-prizes"
              {...choosePrizesButtonBaseProps}
              onClick={onChooseTiersClick}
            />
          </div>
        )}
      </div>
      {!!config?.lty?.enabled && (
        <div className="prizes-config-section__content">
          <Carousel
            slides={loyaltyTierSlides}
            className="prize-slides flex-grow-1"
            baseId="loyalty-tiers"
            enableNavigationAllBreakpoints
          />
        </div>
      )}
    </section>
  );
};

export default LoyaltyTiersSection;
